import type { CSSProperties } from 'react';
import { Truck } from 'lucide-react';

/**
 * THE "BEING WORKED" MARK — one glyph, every surface.
 *
 * A truck (lucide `Truck`, the same one DriverBadge uses) with a ring of arrows
 * orbiting it. It means: a worker is clocked in on this truck right now.
 *
 * It replaced a pulsing dot on the scheduler and it is what the dockview board
 * shows on a door. Those two lived as separate copies of the same SVG for a day
 * and were already at risk of drifting apart, which is the whole reason this
 * file exists: a scheduler and a loader talking about the same truck must be
 * looking at the same symbol (arch doc §1E).
 *
 * WHAT IS SHARED vs WHAT IS PER-SURFACE. The geometry, the arrowheads and the
 * rotation period are shared — those are the identity of the mark. SIZING and
 * the animation MECHANISM stay with the caller, because the two surfaces size
 * things by different systems and forcing one on the other would be worse than
 * the duplication:
 *   - the board is scoped CSS under #db-root, sized off its --f-truck variable,
 *     and animates via the db-orbit keyframes (including a reduced-motion rule);
 *   - the app is Tailwind, sized in rem, animating with `animate-spin`.
 *
 * Rendered in currentColor so each caller sets the colour — white on the board's
 * saturated cards, white on the scheduler's status tiles.
 */

/** Rotation period. One number, so the two surfaces can't drift. */
export const WORK_MARK_SPIN_SECONDS = 2.4;

interface Props {
    /** Class on the wrapper. The caller owns size and colour. */
    wrapClassName: string;
    /** Class on the rotating ring — this is where the animation comes from. */
    ringClassName: string;
    /** Class on the truck glyph, which does NOT rotate. */
    truckClassName: string;
    /**
     * Inline style for the ring. Only pass this where the animation duration
     * must be inline (Tailwind has no arbitrary-duration utility for
     * `animate-spin`). Do NOT pass it on a surface whose stylesheet sets a
     * reduced-motion duration — an inline style outranks the media query and
     * would silently defeat it.
     */
    ringStyle?: CSSProperties;
    /** Ring thickness. Small renderings need a heavier stroke to read at all. */
    ringStrokeWidth?: number;
    truckStrokeWidth?: number;
    /** Hover text. Omit on the board, where nobody has a mouse. */
    title?: string;
}

export function WorkTruckMark({
    wrapClassName,
    ringClassName,
    truckClassName,
    ringStyle,
    ringStrokeWidth = 3.5,
    truckStrokeWidth = 2.4,
    title,
}: Props) {
    return (
        <span className={wrapClassName} title={title} aria-hidden="true">
            <svg className={ringClassName} style={ringStyle} viewBox="0 0 48 48">
                <path d="M24 5 A19 19 0 0 1 43 24" fill="none" stroke="currentColor"
                      strokeWidth={ringStrokeWidth} strokeLinecap="round" />
                <polygon points="39,21 47,21 43,30" fill="currentColor" />
                <path d="M24 43 A19 19 0 0 1 5 24" fill="none" stroke="currentColor"
                      strokeWidth={ringStrokeWidth} strokeLinecap="round" />
                <polygon points="1,27 9,27 5,18" fill="currentColor" />
            </svg>
            <Truck className={truckClassName} strokeWidth={truckStrokeWidth} />
        </span>
    );
}
